import React, { useCallback } from 'react';

import { logger } from '../../lib/logger';
import { Choice } from '../../types/narrative';

import { ChoiceItem } from './ChoiceItem';
import { SectionTitle } from './SectionTitle';

const listLogger = logger.child('[ChoicesList]');

interface ChoicesListProps {
  choices: Choice[] | undefined;
  currentNodeId: string;
  onChoiceUpdate: (choiceIndex: number, field: 'text' | 'npcResponse', value: string) => void;
}

export const ChoicesList = React.memo(function ChoicesList({
  choices,
  currentNodeId,
  onChoiceUpdate,
}: ChoicesListProps) {
  listLogger.debug('Rendered');

  const isReturnToAPlotNode = currentNodeId === 'S2_Return_To_A_Plot_Player_Led';

  const handleTextChange = useCallback(
    (index: number) => (value: string) => onChoiceUpdate(index, 'text', value),
    [onChoiceUpdate]
  );

  const handleNpcResponseChange = useCallback(
    (index: number) => (value: string) => onChoiceUpdate(index, 'npcResponse', value),
    [onChoiceUpdate]
  );

  if (!choices || choices.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 bg-gradient-to-br from-[#161c2e] to-[#1a2040] border border-[#2a3756] rounded-lg p-5 space-y-4 shadow-[0_4px_16px_rgba(0,0,0,0.2)] hover:shadow-[0_6px_24px_rgba(0,0,0,0.3)] transition-all duration-300">
      <SectionTitle title="Player Choices" />
      {isReturnToAPlotNode && (
        <div className="bg-[#1a223a] border border-[#334155] rounded-md p-3 mb-4">
          <p className="text-[#fbbf24] text-sm">
            <span className="font-semibold">⚠️ Important:</span> Choices 1 &amp; 4 lead to a{' '}
            <span className="text-[#10b981] font-semibold">BETTER</span> world state, while choices
            2 &amp; 3 lead to a <span className="text-[#ef4444] font-semibold">WORSE</span> world
            state.
          </p>
        </div>
      )}
      {choices.map((choice, index) => {
        let indicator: '(BETTER)' | '(WORSE)' | null = null;
        if (isReturnToAPlotNode) {
          indicator = index === 0 || index === 3 ? '(BETTER)' : '(WORSE)';
        }
        return (
          <ChoiceItem
            key={choice.id || index}
            choice={choice}
            index={index}
            onChoiceTextChange={handleTextChange(index)}
            onNpcResponseChange={
              choice.npcResponse !== undefined ? handleNpcResponseChange(index) : undefined
            }
            isReturnToAPlotNode={isReturnToAPlotNode}
            worldStateIndicator={indicator}
          />
        );
      })}
    </div>
  );
});
